import { useEffect, useRef, useState } from "react";
import { motion, useInView, animate } from "framer-motion";
import { cn } from "@/lib/utils";

const EASE = [0.16, 1, 0.3, 1];

/* Fade + rise + de-blur on scroll into view */
export function Reveal({ children, delay = 0, y = 24, className }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-10% 0px -10% 0px" });
    return (
        <motion.div
            ref={ref}
            initial={{ opacity: 0, y, filter: "blur(8px)" }}
            animate={
                inView
                    ? { opacity: 1, y: 0, filter: "blur(0px)" }
                    : { opacity: 0, y, filter: "blur(8px)" }
            }
            transition={{ duration: 0.9, ease: EASE, delay }}
            className={className}
        >
            {children}
        </motion.div>
    );
}

/* Masked line-by-line reveal for headings. Pass an array of lines. */
export function MaskedHeading({ lines, className, as: Tag = "h1", delay = 0 }) {
    return (
        <Tag className={className}>
            {lines.map((line, i) => (
                <span
                    key={i}
                    className="block overflow-hidden pb-[0.12em] -mb-[0.12em]"
                >
                    <motion.span
                        className="block will-change-transform"
                        initial={{ y: "110%" }}
                        animate={{ y: "0%" }}
                        transition={{
                            duration: 0.95,
                            ease: EASE,
                            delay: delay + i * 0.09,
                        }}
                    >
                        {line}
                    </motion.span>
                </span>
            ))}
        </Tag>
    );
}

export function Eyebrow({ children, className }) {
    return (
        <span
            className={cn(
                "inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.22em] text-muted-foreground",
                className,
            )}
        >
            <span className="h-px w-6 bg-navy/60" />
            {children}
        </span>
    );
}

export function CountUp({ value, suffix = "", duration = 1.8 }) {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-15% 0px" });
    const [display, setDisplay] = useState(0);

    useEffect(() => {
        if (!inView) return;
        const controls = animate(0, value, {
            duration,
            ease: [0.16, 1, 0.3, 1],
            onUpdate: (v) => setDisplay(Math.round(v)),
        });
        return () => controls.stop();
    }, [inView, value, duration]);

    return (
        <span ref={ref}>
            {display.toLocaleString()}
            {suffix}
        </span>
    );
}

export function Marquee({ items }) {
    const content = [...items, ...items];
    return (
        <div className="mask-fade-x overflow-hidden border-y border-border py-5">
            <div className="flex w-max animate-marquee items-center gap-10 whitespace-nowrap">
                {content.map((item, i) => (
                    <span
                        key={i}
                        className="flex items-center gap-10 font-mono text-sm uppercase tracking-[0.2em] text-muted-foreground"
                    >
                        {item}
                        <span className="text-navy">◆</span>
                    </span>
                ))}
            </div>
        </div>
    );
}
